/**
 * Creative Trends 类型定义
 * 与 API 响应结构对齐
 *
 * 主键约定：使用 `(game_id, topic)` 二元组作为 trend 的唯一标识。
 *           前端工具：trendKey(item) → "${game_id}__${topic}"  仅用于 React key / selectedKey 比较
 *           调用 detail / related_creatives 时回传 game_id + topic 两个字段
 */

export type TStatusTab = 'hot' | 'rising' | 'new';
export type TViewMode = 'card' | 'table';

/**
 * 环比时间窗口（list.md 升级后结构，与 ad_creative_multi 等接口一致）
 * 当期 = [new_start_date, new_end_date]，基准期 = [old_start_date, old_end_date]
 */
export interface IKPILinkTime {
  new_start_date: string;
  new_end_date: string;
  old_start_date: string;
  old_end_date: string;
}

/**
 * KPI 值结构（与 List/Detail API 共用）
 * 前端计算环比：ratio = (value - old_value) / old_value
 *
 * link_time 兼容两种结构：
 *  - List 接口（最新）：4 字段对象 IKPILinkTime
 *  - Detail 接口（旧）：单字符串 "YYYY-MM-DD"（基准期结束日）
 *  环比计算异常降级时缺省（不下发）。
 */
export interface IKPIValue {
  /** 当期值 */
  value: number;
  /** 基准期值（用于计算 DoD 环比；无数据时为 0） */
  old_value: number;
  /** 基准期日期信息（对象 = list 新结构 / 字符串 = detail 旧结构 / 缺省 = 降级） */
  link_time?: string | IKPILinkTime;
}

/**
 * List 接口 max 字段（当前 tab + filter 下全量 KPI 最大值）
 * 用途：Card View 进度条归一化。不做环比。
 *
 * 字段名由各业务页（CreativeTrends / UgcCreativeTrends 等）按各自 API 决定，
 * 这里通过 Record 兼容任意字段，业务页可在自己的 types 里定义更窄的子类型。
 */
export type IListMax = Record<string, number | undefined>;

/**
 * 列表项中的 Top3 缩略图（卡片/表格预览用）
 * 字段对齐 list.md 文档 top_related_creatives
 */
export interface ITopCreative {
  video_url: string;
  /** 预览图（与 ICreativeItem 同字段；list 内嵌目前不下发，预留兼容） */
  preview_img?: string;
  /** 原始内容 URL（与 ICreativeItem 同字段；list 内嵌目前不下发，预留兼容） */
  origin_url?: string;
  /** 视频时长（秒） */
  video_duration?: number;
  /** 渠道列表（list.md：`channel: string[]`） */
  channel?: string[];
  /** 国家列表（list.md：`countries: string[]`） */
  countries?: string[];
  /** 累计 impressions */
  impression_number?: number;
  /** 累计 likes */
  like_count?: number;
  /** 首次发现日期 "YYYY-MM-DD HH:mm:ss" */
  first_seen?: string;
  /** 最后发现日期 "YYYY-MM-DD HH:mm:ss" */
  last_seen?: string;
}

/**
 * 趋势列表项基础结构（List API 返回）
 * 主键 = (game_id, topic)
 *
 * KPI 字段 / 缩略图字段由业务页通过泛型扩展：
 *  - CreativeTrends：扩展 cumulative_impressions / creatives / avg_days_active + top_related_creatives
 *  - UgcCreativeTrends：扩展 tweets_view / tweets_like / videos + top_related_videos
 *
 * 共享组件（TrendCard / ListView / TrendDrawer / TrendInfoRows）以 ITrendItemBase 为入参，
 * 通过 props（kpiConfigs / thumbnailsAccessor / metricColumns）注入实际字段名。
 */
export interface ITrendItemBase {
  game_id: string;
  topic: string;
  /** 后端硬编码（CreativeTrends='ua' / UgcCreativeTrends='ugc'），前端原样回传 */
  /** topic 英文文案（后端按多语言下发） */
  topic_en?: string;
  /** 后端硬编码 'ua'，前端原样回传 */
  source_type?: string;
  topic_type_l1: string;
  topic_type_l1_en: string;
  topic_type_l2: string;
  topic_type_l2_en: string;
  status: string;
  /** 游戏名（来自 ADGame cond，前端 join 处理） */
  game_name?: string;
  /** 游戏封面 URL */
  game_cover?: string;
  ai_summary?: string;
  ai_summary_en?: string;
}

/**
 * CreativeTrends 业务下的趋势项
 */
export interface ITrendItem extends ITrendItemBase {
  cumulative_impressions: IKPIValue;
  creatives: IKPIValue;
  avg_days_active: IKPIValue;
  ai_summary: string;
  ai_summary_en: string;
  top_related_creatives: ITopCreative[];
}

/**
 * 趋势详情（Detail API 返回）
 * 字段口径与 List 完全一致（按 details.md：本期不返回 inspirations / related_total_count）
 */
export interface ITrendDetail {
  game_id: string;
  topic: string;
  source_type?: string;
  topic_type_l1: string;
  topic_type_l1_en: string;
  topic_type_l2: string;
  topic_type_l2_en: string;
  status: string;
  game_name?: string;
  game_cover?: string;
  cumulative_impressions: IKPIValue;
  creatives: IKPIValue;
  avg_days_active: number;
  ai_summary: string;
  ai_summary_en: string;
}

/**
 * 素材项（Related Creatives API 返回，对齐 /materials/list MaterialItem）
 */
export interface ICreativeItem {
  /** 素材关联键 */
  creative_id?: string;
  app_name?: string;
  app_logo?: string;
  title?: string;
  body?: string;
  preview_img?: string;
  video_url: string;
  video_duration?: number;
  origin_url?: string;
  html_url?: string;
  store_url?: string;
  cta_url?: string;
  resources?: string;
  type?: string[];
  platform?: string[];
  channel?: string[];
  region?: string[];
  countries?: string[];
  category?: string[];
  source_app?: string[];
  /** 累计曝光 */
  impression_number?: number;
  like_count?: number;
  comment_count?: number;
  share_count?: number;
  view_count?: number;
  interaction_count?: number;
  first_seen?: string;
  last_seen?: string;
  days?: number;
  game_id?: string;
  material_count?: number;
}

/**
 * Stats API 返回（Tab 命中数）
 */
export interface ICreativeTrendsStats {
  hot: number;
  rising: number;
  new: number;
}

/**
 * 排序选项
 */
export interface ISortOption {
  value: string;
  text: string;
  textCn: string;
  order: 'asc' | 'desc';
}

/**
 * 工具函数：根据 (game_id, topic) 生成稳定的唯一 key
 * 用于 React key / selectedTrendKey 比较；不参与服务端通信
 */
export function trendKey(
  item?: {
    game_id?: string;
    topic?: string;
  } | null,
): string {
  if (!item) return '';
  return `${item.game_id || ''}__${item.topic || ''}`;
}
